import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useAdminLocationGroupTranslations, useAdminLocationTranslations, useAdminReferenceOptionTranslations, useDeleteAdminLocationGroupTranslation, useDeleteAdminLocationTranslation, useDeleteAdminReferenceOptionTranslation, useSaveAdminLocationGroupTranslation, useSaveAdminLocationTranslation, useSaveAdminReferenceOptionTranslation, } from '@/api/opengater/useLocations';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { SettingsDanger, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import { locationAdminError, locationFieldErrors, requiredTextError } from './locationRules';
import s from '../OpengaterLocations.module.css';
const LOCALES = ['ru', 'en', 'hy', 'tr'];
const emptyDraft = () => ({ name: '', description: '' });
const emptyDrafts = () => ({
    ru: emptyDraft(),
    en: emptyDraft(),
    hy: emptyDraft(),
    tr: emptyDraft(),
});
const draftOf = (translation) => ({
    name: translation?.name ?? '',
    description: translation?.description ?? '',
});
const sameDraft = (left, right, supportsDescription) => left.name === right.name && (!supportsDescription || left.description === right.description);
function TranslationEditor({ translations, loading, loadError, supportsDescription, saving, deleting, onSave, onDelete, onDirtyChange, }) {
    const [locale, setLocale] = useState('ru');
    const [base, setBase] = useState(emptyDrafts);
    const [drafts, setDrafts] = useState(emptyDrafts);
    const [fieldErrors, setFieldErrors] = useState({});
    const [actionError, setActionError] = useState(null);
    useEffect(() => {
        if (!translations)
            return;
        const authoritative = emptyDrafts();
        for (const language of LOCALES) {
            authoritative[language] = draftOf(translations.find((translation) => translation.language === language));
        }
        setBase((currentBase) => {
            setDrafts((currentDrafts) => {
                const next = { ...currentDrafts };
                for (const language of LOCALES) {
                    if (sameDraft(currentDrafts[language], currentBase[language], supportsDescription)) {
                        next[language] = authoritative[language];
                    }
                }
                return next;
            });
            return authoritative;
        });
    }, [supportsDescription, translations]);
    const dirtyLocales = useMemo(() => LOCALES.filter((language) => !sameDraft(drafts[language], base[language], supportsDescription)), [base, drafts, supportsDescription]);
    const dirty = dirtyLocales.includes(locale);
    const anyDirty = dirtyLocales.length > 0;
    const current = drafts[locale];
    const existing = translations?.find((translation) => translation.language === locale) ?? null;
    const nameValidation = requiredTextError(current.name);
    const nameError = fieldErrors.name ?? (dirty ? nameValidation : null);
    useEffect(() => onDirtyChange(anyDirty), [anyDirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    const syncLocale = (nextTranslations) => {
        const next = draftOf(nextTranslations.find((translation) => translation.language === locale));
        setBase((currentBase) => ({ ...currentBase, [locale]: next }));
        setDrafts((currentDrafts) => ({ ...currentDrafts, [locale]: next }));
    };
    const save = async () => {
        if (nameValidation || saving)
            return;
        try {
            const refreshed = await onSave({
                language: locale,
                name: current.name.trim(),
                description: supportsDescription ? current.description.trim() || null : null,
            });
            syncLocale(refreshed);
            setFieldErrors({});
            setActionError(null);
        }
        catch (error) {
            const fields = locationFieldErrors(error, ['name', 'description']);
            setFieldErrors(fields);
            setActionError(Object.keys(fields).length
                ? null
                : locationAdminError(error, 'Не удалось сохранить перевод.'));
        }
    };
    const remove = async () => {
        try {
            const refreshed = await onDelete(locale);
            syncLocale(refreshed);
            setFieldErrors({});
            setActionError(null);
        }
        catch (error) {
            setActionError(locationAdminError(error, 'Не удалось удалить перевод.'));
        }
    };
    if (loading)
        return _jsx(SettingsLoading, {});
    if (loadError && !translations) {
        return (_jsx(SettingsNote, { tone: "error", children: locationAdminError(loadError, 'Не удалось загрузить переводы.') }));
    }
    return (_jsxs("div", { className: s.translationEditor, children: [_jsx("div", { className: s.translationTabs, children: _jsx(SegmentedControl, { value: locale, onChange: (language) => {
                        setLocale(language);
                        setFieldErrors({});
                        setActionError(null);
                    }, options: LOCALES.map((language) => ({
                        value: language,
                        label: language.toUpperCase(),
                    })) }) }), _jsxs(SettingsGroup, { title: `Перевод · ${locale.toUpperCase()}`, footer: existing ? 'Перевод настроен' : 'Для этого языка перевод ещё не настроен', children: [_jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, error: nameError, children: _jsx(Input, { "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043F\u0435\u0440\u0435\u0432\u043E\u0434\u0430", "aria-invalid": Boolean(nameError), value: current.name, disabled: saving || deleting, onChange: (event) => {
                                const name = event.target.value;
                                setDrafts((currentDrafts) => ({
                                    ...currentDrafts,
                                    [locale]: { ...currentDrafts[locale], name },
                                }));
                                setFieldErrors((currentErrors) => ({ ...currentErrors, name: undefined }));
                                setActionError(null);
                            } }) }), supportsDescription && (_jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, error: fieldErrors.description, children: _jsx(Input, { "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043F\u0435\u0440\u0435\u0432\u043E\u0434\u0430", value: current.description, disabled: saving || deleting, onChange: (event) => {
                                const description = event.target.value;
                                setDrafts((currentDrafts) => ({
                                    ...currentDrafts,
                                    [locale]: { ...currentDrafts[locale], description },
                                }));
                                setFieldErrors((currentErrors) => ({
                                    ...currentErrors,
                                    description: undefined,
                                }));
                                setActionError(null);
                            } }) }))] }), actionError && _jsx(SettingsNote, { tone: "error", children: actionError }), _jsxs("div", { className: s.translationActions, children: [_jsx(Button, { type: "button", variant: "ghost", disabled: !dirty || saving || deleting, onClick: () => {
                            setDrafts((currentDrafts) => ({ ...currentDrafts, [locale]: base[locale] }));
                            setFieldErrors({});
                            setActionError(null);
                        }, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u043F\u0440\u0430\u0432\u043A\u0438 \u043F\u0435\u0440\u0435\u0432\u043E\u0434\u0430" }), _jsx(Button, { type: "button", disabled: !dirty || Boolean(nameValidation) || saving || deleting, onClick: save, children: saving ? 'Сохраняем…' : 'Сохранить перевод' })] }), existing && (_jsx(SettingsDanger, { label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u043F\u0435\u0440\u0435\u0432\u043E\u0434", confirmText: `Удалить перевод ${locale.toUpperCase()}?`, pending: deleting, disabled: dirty, error: undefined, onConfirm: remove }))] }));
}
export function LocationTranslationEditor({ id, onDirtyChange, }) {
    const query = useAdminLocationTranslations(id);
    const save = useSaveAdminLocationTranslation();
    const remove = useDeleteAdminLocationTranslation();
    return (_jsx(TranslationEditor, { translations: query.data, loading: query.isLoading, loadError: query.error, supportsDescription: true, saving: save.isPending, deleting: remove.isPending, onDirtyChange: onDirtyChange, onSave: async (body) => {
            await save.mutateAsync({ id, body });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        }, onDelete: async (language) => {
            await remove.mutateAsync({ id, language });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        } }));
}
export function LocationGroupTranslationEditor({ id, onDirtyChange, }) {
    const query = useAdminLocationGroupTranslations(id);
    const save = useSaveAdminLocationGroupTranslation();
    const remove = useDeleteAdminLocationGroupTranslation();
    return (_jsx(TranslationEditor, { translations: query.data, loading: query.isLoading, loadError: query.error, supportsDescription: false, saving: save.isPending, deleting: remove.isPending, onDirtyChange: onDirtyChange, onSave: async (body) => {
            await save.mutateAsync({ id, body: { language: body.language, name: body.name } });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        }, onDelete: async (language) => {
            await remove.mutateAsync({ id, language });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        } }));
}
export function ReferenceOptionTranslationEditor({ id, onDirtyChange, }) {
    const query = useAdminReferenceOptionTranslations(id);
    const save = useSaveAdminReferenceOptionTranslation();
    const remove = useDeleteAdminReferenceOptionTranslation();
    return (_jsx(TranslationEditor, { translations: query.data, loading: query.isLoading, loadError: query.error, supportsDescription: true, saving: save.isPending, deleting: remove.isPending, onDirtyChange: onDirtyChange, onSave: async (body) => {
            await save.mutateAsync({ id, body });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        }, onDelete: async (language) => {
            await remove.mutateAsync({ id, language });
            return (await query.refetch({ throwOnError: true })).data ?? [];
        } }));
}
